/**
 * Secure logging utility that prevents accidental exposure of sensitive data
 * and removes console logs in production builds
 */

interface LogLevel {
  debug: 0;
  info: 1;
  warn: 2;
  error: 3;
}

const LOG_LEVELS: LogLevel = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3
};

// Sensitive data patterns to redact
const SENSITIVE_PATTERNS = [
  /password/i,
  /secret/i,
  /token/i,
  /key/i,
  /credential/i,
  /code/i, // verification codes
  /pin/i,
  /otp/i,
  /auth/i
];

// Fields that should be completely redacted
const SENSITIVE_FIELDS = new Set([
  'password',
  'confirmPassword',
  'newPassword',
  'oldPassword',
  'token',
  'accessToken',
  'refreshToken',
  'secret',
  'privateKey',
  'code',
  'verificationCode',
  'confirmationCode',
  'otp',
  'pin',
  'cvv',
  'ssn',
  'socialSecurityNumber'
]);

/**
 * Redacts sensitive information from objects and strings
 */
function redactSensitiveData(data: unknown): unknown {
  if (typeof data === 'string') {
    // Check if the string contains sensitive patterns
    for (const pattern of SENSITIVE_PATTERNS) {
      if (pattern.test(data)) {
        return '[REDACTED]';
      }
    }
    return data;
  }

  if (typeof data === 'object' && data !== null) {
    if (Array.isArray(data)) {
      return data.map(item => redactSensitiveData(item));
    }

    const result: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      // Check if the field name is sensitive
      if (SENSITIVE_FIELDS.has(key.toLowerCase())) {
        result[key] = '[REDACTED]';
      } else {
        result[key] = redactSensitiveData(value);
      }
    }
    return result;
  }

  return data;
}

// Log context for tracking request flow
interface LogContext {
  userId?: string;
  sessionId?: string;
  requestId?: string;
  correlationId?: string;
}

// Authentication event types
type AuthEvent =
  | 'USER_LOGIN_ATTEMPT'
  | 'USER_LOGIN_SUCCESS'
  | 'USER_LOGIN_FAILED'
  | 'USER_LOGOUT'
  | 'USER_AUTHENTICATED'
  | 'TOKEN_REFRESHED'
  | 'SESSION_EXPIRED'
  | 'PERMISSION_DENIED'
  | 'IMPERSONATION_START'
  | 'IMPERSONATION_END';

/**
 * Secure logger class that prevents sensitive data exposure
 */
class SecureLogger {
  private currentLevel: keyof LogLevel;
  private isDevelopment: boolean;
  private context: LogContext = {};

  constructor() {
    this.currentLevel = import.meta.env.DEV ? 'debug' : 'error';
    this.isDevelopment = import.meta.env.DEV;
  }

  private shouldLog(level: keyof LogLevel): boolean {
    return LOG_LEVELS[level] >= LOG_LEVELS[this.currentLevel];
  }

  private formatMessage(level: string, message: string, ...args: unknown[]): void {
    if (!this.isDevelopment && level !== 'error') {
      // In production, only log errors to avoid performance impact
      return;
    }

    const timestamp = new Date().toISOString();
    const redactedArgs = args.map(arg => redactSensitiveData(arg));

    const logMethod = level === 'error' ? console.error :
                     level === 'warn' ? console.warn :
                     console.log;

    logMethod(`[${timestamp}] ${level.toUpperCase()}: ${message}`, ...redactedArgs);
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      this.formatMessage('debug', message, ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      this.formatMessage('info', message, ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      this.formatMessage('warn', message, ...args);
    }
  }

  error(message: string, ...args: unknown[]): void {
    if (this.shouldLog('error')) {
      this.formatMessage('error', message, ...args);
    }
  }

  /**
   * Set context for all subsequent log messages
   */
  setContext(context: LogContext): void {
    this.context = { ...this.context, ...context };
  }

  /**
   * Clear the logging context
   */
  clearContext(): void {
    this.context = {};
  }

  /**
   * Sanitize data for PII (Personally Identifiable Information)
   */
  private sanitize(data: any): any {
    if (!data) return data;

    // Deep clone to avoid modifying original data
    const cloned = JSON.parse(JSON.stringify(data));

    // PII fields to mask partially
    const piiFields = ['email', 'phone', 'cpf', 'name', 'fullName'];

    const maskPII = (obj: any): any => {
      if (typeof obj !== 'object' || obj === null) return obj;

      for (const key in obj) {
        const lowerKey = key.toLowerCase();

        // Check if it's a sensitive field (complete redaction)
        if (SENSITIVE_FIELDS.has(lowerKey)) {
          obj[key] = '[REDACTED]';
        }
        // Check if it's a PII field (partial masking)
        else if (piiFields.some(field => lowerKey.includes(field))) {
          if (typeof obj[key] === 'string' && obj[key].length > 4) {
            // Keep first 2 and last 2 characters, mask the rest
            const val = obj[key];
            obj[key] = val.substring(0, 2) + '***' + val.substring(val.length - 2);
          } else {
            obj[key] = '[MASKED]';
          }
        }
        // Recursively sanitize nested objects
        else if (typeof obj[key] === 'object') {
          obj[key] = maskPII(obj[key]);
        }
      }

      return obj;
    };

    return maskPII(cloned);
  }

  /**
   * Logs authentication events with extra security measures
   */
  authEvent(event: AuthEvent, metadata?: Record<string, unknown>): void {
    // Log auth events in both dev and production for audit trail
    const contextData = { ...this.context };
    const sanitizedMetadata = this.sanitize(metadata);

    // In production, only log critical auth events to error channel
    if (!this.isDevelopment) {
      if (['USER_LOGIN_FAILED', 'PERMISSION_DENIED', 'SESSION_EXPIRED'].includes(event)) {
        this.error(`AUTH_EVENT: ${event}`, { context: contextData, metadata: sanitizedMetadata });
      }
      return;
    }

    this.info(`🔐 AUTH: ${event}`, { context: contextData, metadata: sanitizedMetadata });
  }

  /**
   * Log performance metrics for authentication operations
   */
  authPerformance(operation: string, durationMs: number, success: boolean): void {
    if (!this.isDevelopment) return;

    const level = durationMs > 1000 ? 'warn' : 'info';
    const message = `⏱️ AUTH_PERF: ${operation} took ${durationMs}ms (${success ? 'success' : 'failed'})`;

    if (level === 'warn') {
      this.warn(message, { context: this.context });
    } else {
      this.info(message, { context: this.context });
    }
  }

  /**
   * Logs API calls without exposing sensitive headers or data
   */
  apiCall(method: string, url: string, headers?: Record<string, string>): void {
    if (!this.isDevelopment) return;

    const sanitizedHeaders = headers ? {
      ...headers,
      authorization: headers.authorization ? '[REDACTED]' : undefined,
      'x-api-key': headers['x-api-key'] ? '[REDACTED]' : undefined,
      cookie: headers.cookie ? '[REDACTED]' : undefined
    } : {};

    this.debug(`🌐 API: ${method} ${url}`, { headers: sanitizedHeaders });
  }
}

// Export singleton instance
export const secureLogger = new SecureLogger();

// Export individual log functions for convenience
export const { debug, info, warn, error, authEvent, apiCall, setContext, clearContext, authPerformance } = secureLogger;

// Export types for use in other modules
export type { LogContext, AuthEvent };